import { apiUtils } from '../utils/api';
import { jwtDecode } from 'jwt-decode';
import { storageUtils } from '../utils/storage';
import { API_ENDPOINTS, SUCCESS_MESSAGES, ERROR_MESSAGES } from '../constants';

class AuthService {
  constructor() {
    this.isAuthenticated = this.checkAuthStatus();
  }

  checkAuthStatus() {
    const token = storageUtils.getAuthToken();
    return !!token && !this.isTokenExpired(token);
  }

  isTokenExpired(token) {
    try {
      const payload = jwtDecode(token);
      return payload.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  }

  async login(credentials) {
    try {
      const response = await apiUtils.post(API_ENDPOINTS.AUTH.LOGIN, credentials);
      if (response?.requires_2fa) {
        return {
          success: false,
          requires_2fa: true,
          otp_session_id: response.otp_session_id,
          masked_phone: response.masked_phone,
          masked_email: response.masked_email,
          message: response.message || 'Verification required.',
        };
      }
      return {
        success: true,
        message: response.message || SUCCESS_MESSAGES.LOGIN_SUCCESS,
        access: response.access,
        refresh: response.refresh,
        user: response.user,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || ERROR_MESSAGES.LOGIN_FAILED,
      };
    }
  }

  async verifyTwoFactor(payload) {
    try {
      const response = await apiUtils.post(API_ENDPOINTS.AUTH.VERIFY_2FA, payload);
      return {
        success: true,
        access: response.access,
        refresh: response.refresh,
        user: response.user,
        message: response.message || SUCCESS_MESSAGES.LOGIN_SUCCESS,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || ERROR_MESSAGES.LOGIN_FAILED,
      };
    }
  }

  async resendTwoFactor(payload) {
    try {
      const response = await apiUtils.post(API_ENDPOINTS.AUTH.RESEND_2FA, payload);
      return {
        success: true,
        otp_session_id: response.otp_session_id,
        masked_phone: response.masked_phone,
        masked_email: response.masked_email,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Unable to resend verification code.',
      };
    }
  }

  async register(payload) {
    try {
      const response = await apiUtils.post(API_ENDPOINTS.AUTH.REGISTER, payload);
      return {
        success: true,
        message: SUCCESS_MESSAGES.REGISTER_SUCCESS,
        user: response,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || ERROR_MESSAGES.REGISTER_FAILED,
      };
    }
  }

  async forgotPassword(identifier) {
    try {
      await apiUtils.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { username: identifier });
      return {
        success: true,
        message: 'If an account exists, a reset link has been emailed.',
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Unable to process password reset request.',
      };
    }
  }

  async resetPassword({ uid, token, password, confirmPassword }) {
    try {
      await apiUtils.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
        uid,
        token,
        password,
        confirm_password: confirmPassword,
      });
      return {
        success: true,
        message: 'Password updated successfully.',
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Unable to reset password.',
      };
    }
  }

  async changePassword({ currentPassword, newPassword, confirmPassword }) {
    try {
      const response = await apiUtils.post(API_ENDPOINTS.USER.CHANGE_PASSWORD, {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      return {
        success: true,
        forceLogout: !!response.force_logout,
        message: response.message || SUCCESS_MESSAGES.PASSWORD_CHANGED,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Unable to change password.',
      };
    }
  }

  async requestEmailChange({ currentPassword, newEmail }) {
    try {
      const response = await apiUtils.post(API_ENDPOINTS.USER.REQUEST_EMAIL_CHANGE, {
        current_password: currentPassword,
        new_email: newEmail,
      });
      return {
        success: true,
        message: response.message || 'We sent a confirmation link to your new email address.',
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Unable to request email change.',
      };
    }
  }

  async confirmEmailChange({ token }) {
    try {
      const response = await apiUtils.post(API_ENDPOINTS.USER.CONFIRM_EMAIL_CHANGE, { token });
      return {
        success: true,
        forceLogout: !!response.force_logout,
        message: response.message || 'Email updated successfully.',
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Unable to confirm email change.',
      };
    }
  }

  async refreshAccessToken() {
    try {
      const refreshToken = storageUtils.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      const response = await apiUtils.post(API_ENDPOINTS.AUTH.REFRESH, { refresh: refreshToken });
      const { access, refresh: newRefresh } = response;
      storageUtils.setAuthToken(access);
      // SimpleJWT rotates refresh tokens (ROTATE_REFRESH_TOKENS=True) and
      // blacklists the old one. If we don't save the new one, the next
      // refresh attempt uses the now-blacklisted token and logs the user out.
      if (newRefresh) {
        storageUtils.setItem('refresh_token', newRefresh);
      }
      this.isAuthenticated = true;
      return { access, refresh: newRefresh };
    } catch (error) {
      storageUtils.clearAuth?.();
      storageUtils.removeAuthToken?.();
      storageUtils.removeItem?.('refresh_token');
      this.isAuthenticated = false;
      throw error;
    }
  }

  async logout() {
    // Blacklist the refresh token server-side so it can't be reused
    // (e.g. by anyone who got hold of it). Done BEFORE clearing local
    // storage so the token is still available to send. Silent on
    // failure so a network problem doesn't block the local logout.
    const refreshToken = storageUtils.getItem?.('refresh_token');
    if (refreshToken) {
      try {
        await apiUtils.post(API_ENDPOINTS.AUTH.LOGOUT, { refresh: refreshToken });
      } catch {
        // Server unreachable — proceed with local logout anyway.
      }
    }
    this.isAuthenticated = false;
    storageUtils.clearAuth?.();
    storageUtils.removeItem?.('refresh_token');
    // Clear failed-login attempt tracking so a fresh login attempt
    // doesn't carry over the counter from before logout.
    storageUtils.removeItem?.('dsl-login-attempt-count');
    storageUtils.removeItem?.('dsl-login-lock-until');
  }
}

const authService = new AuthService();
export default authService;
